import styled from "@emotion/styled";
import { useCallback } from "react";
import * as React from "react";
import { useDispatch } from "react-redux";

import { ModalPortal, Text } from "@livechat/design-system-react-components";

import IframeLink from "components/external-link/IframeLink";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { SpamActions } from "store/entities/spam/actions";
import { StackingContextLevel } from "styles/stacking-context-level";
import { plural } from "utils/string";

interface ISpamListModalProps {
  modalType: "spam" | "unspam";
  requesterEmails: string[];
  onClose(): void;
}

const StyledLink = styled(IframeLink)`
  color: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.theme.linkColor
  };
  text-decoration: none;

  &:visited,
  &:inactive {
    color: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.linkColor
    };
  }

  &:hover,
  &:active,
  &:focus {
    text-decoration: underline;
  }
`;

const SpamListModal: React.FC<ISpamListModalProps> = ({ modalType, requesterEmails, onClose }) => {
  const dispatch = useDispatch();
  const handleAdd = useCallback(() => {
    if (modalType === "spam") {
      trackEngagement("Ticket: Blocked list addresses added through modal", EventPlace.Tickets, {
        emailsCount: requesterEmails.length,
      });
      if (requesterEmails.length === 1) {
        dispatch(SpamActions.addBlockedEntry({ entry: requesterEmails[0] }));
      } else {
        dispatch(SpamActions.queuedAddBlockedEntries({ requesterEmails }));
      }
    } else {
      trackEngagement("Ticket: Trusted list addresses added through modal", EventPlace.Tickets, {
        emailsCount: requesterEmails.length,
      });
      if (requesterEmails.length === 1) {
        dispatch(SpamActions.addTrustedEntry({ entry: requesterEmails[0] }));
      } else {
        dispatch(SpamActions.queuedAddTrustedEntries({ requesterEmails }));
      }
    }
    onClose();
  }, [dispatch, modalType, onClose, requesterEmails]);

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <ConfirmActionModal
        onClose={onClose}
        heading={`Add requester's ${plural(requesterEmails.length, "address", "addresses")} to ${
          modalType === "spam" ? "blocked" : "trusted"
        }?`}
        primaryAction={{
          label: `Add to ${modalType === "spam" ? "blocked" : "trusted"}`,
          onClick: handleAdd,
          actionType: "primary",
          dataTestId: "delete-modal-button",
        }}
        secondaryAction={{
          label: "Not this time",
          onClick: onClose,
          dataTestId: "spam-modal-not-this-time-button",
        }}
      >
        {modalType === "unspam" && (
          <Text textAlign="center" css={{ maxWidth: "360px" }}>
            If you add to the <StyledLink to="/settings/spam#trusted">Trusted list</StyledLink>, all future tickets from
            this address won&lsquo;t be marked as spam.
          </Text>
        )}
        {modalType === "spam" && (
          <Text textAlign="center" css={{ maxWidth: "360px" }}>
            If you add to <StyledLink to="/settings/spam#blocked">Blocked list</StyledLink>, all future tickets from
            that address will be marked as spam.
          </Text>
        )}
      </ConfirmActionModal>
    </ModalPortal>
  );
};

export default SpamListModal;
